import React, { useState } from 'react';
import { QueueJob, QueueStatus } from '../../types/queue';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { IconName } from '../Icon/Icon.type';
import { Avatar } from '../Avatar/Avatar';
import { FileTypeIcon } from '../DocumentsPanel/components/FileTypeIcon';
import { getRelativeTime } from '../../utils/dateUtils';
import { AGENT_SETTINGS_CONFIG } from '../AgentSettingsMenu/AgentSettingsMenu.types';
import { stripTagMarkers } from '../../utils/cabTagSerializer';
import { TaggedContent } from '../TaggedContent/TaggedContent';

const PREVIEW_BUDGET = 140;

interface QueuedMessageBannerProps {
  jobs: QueueJob[];
  onEdit: (jobId: string) => void;
  onCancel: (jobId: string) => void;
  onResumeAll?: () => void;
  /** The job_id currently being edited (null if no edit is in progress) */
  editingJobId: string | null;
  /** True if the viewer has project edit rights (can manage ALL cards + Resume). */
  canEditProject?: boolean;
  /** Current user's id — a user can always manage their OWN queued cards. */
  currentUserId?: string;
}

/** Human label for an agent setting key (falls back to a prettified key). */
const settingLabel = (key: string): string =>
  AGENT_SETTINGS_CONFIG.find(c => c.key === key)?.label ?? key.replace(/_/g, ' ');

/** Small inline attachment chip — mirrors the FilePreview chips in the chat input. */
const AttachmentChip: React.FC<{ name: string; fileType?: string }> = ({ name, fileType }) => (
  <span className="inline-flex items-center gap-bg-v2-xs rounded-md px-1.5 py-0.5 bg-bg-primary border border-border-secondary max-w-[160px]">
    <FileTypeIcon extension={fileType || name.split('.').pop() || ''} size={12} />
    <span className="text-[10px] font-medium text-text-secondary truncate">{name}</span>
  </span>
);

/**
 * A single queued-prompt card.
 *
 * Layout mirrors the chat user-message bubble: an avatar of whoever queued the
 * prompt, their name + a relative timestamp, the message text, any attachments,
 * the model/agent-setting badges, and Edit / Remove actions.
 */
const QueueCard: React.FC<{
  job: QueueJob;
  isEditing: boolean;
  isFailed: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
  onCancel: () => void;
  /** Whether the current viewer may edit/cancel THIS card (owner or project editor). */
  canManage: boolean;
}> = ({ job, isEditing, isFailed, isExpanded, onToggleExpand, onEdit, onCancel, canManage }) => {
  const isPaused = job.status === QueueStatus.PAUSED;
  // Base the "Show more" decision on the display length (chips render as their
  // short label, not the long {{ticket:...}} token).
  const isLong = stripTagMarkers(job.user_message).length > PREVIEW_BUDGET;

  const user = job.user_info ?? {};
  const displayName =
    user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.username || 'Unknown user';

  // Only surface real Agent settings as chips. Internal flags like `ask_mode`
  // live in `settings` too but are shown via the dedicated interaction-mode badge,
  // so they must not render as stray setting chips.
  const activeSettings = job.settings
    ? Object.entries(job.settings)
        .filter(([k, v]) => v && AGENT_SETTINGS_CONFIG.some(c => c.key === k))
        .map(([k]) => k)
    : [];
  const attachments = job.attachments ?? [];

  return (
    <div
      className={`group relative rounded-xl border px-3 py-2.5 transition-colors duration-200
        ${isEditing
          ? 'border-border-brand bg-bg-brand-primary-alt'
          : isFailed
            ? 'border-border-error bg-bg-error-secondary'
            : isPaused
              ? 'border-border-error-subtle bg-bg-error-secondary'
              : 'border-border-secondary bg-bg-primary hover:border-border-brand'
        }`}
    >
      <div className="flex items-start gap-2.5">
        {/* Author avatar */}
        <div className="flex-shrink-0 mt-0.5">
          {(user.firstName || user.username) ? (
            <Avatar
              user={user as any}
              size="sm"
              variant="brand"
              title={displayName}
            />
          ) : (
            <span className="inline-flex items-center justify-center w-bg-v2-4xl h-bg-v2-4xl rounded-bg-v2-full bg-bg-tertiary text-text-tertiary">
              <Icon name={IconName.User} size="xs" inheritColor />
            </span>
          )}
        </div>

        {/* Main column */}
        <div className="flex-1 min-w-0">
          {/* Meta row: name • time, status badge on the right */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-xs font-semibold text-text-primary truncate">{displayName}</span>
              {job.created_at && (
                <>
                  <span className="text-text-quaternary text-[10px]">•</span>
                  <span className="text-[10px] text-text-tertiary whitespace-nowrap">
                    {getRelativeTime(job.created_at)}
                  </span>
                </>
              )}
            </div>

            {/* Status / position badge */}
            <div className="flex-shrink-0">
              {isEditing ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-bg-brand-solid text-text-primary-on-brand text-[10px] font-semibold">
                  <span className="animate-pulse">●</span> Editing
                </span>
              ) : isFailed ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-bg-error-primary text-text-error-primary border border-border-error-subtle text-[10px] font-semibold">
                  Failed
                </span>
              ) : isPaused ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-bg-error-primary text-text-error-primary border border-border-error-subtle text-[10px] font-semibold">
                  Paused
                </span>
              ) : (
                <span
                  className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-bg-brand-primary-alt text-text-brand-primary border border-border-brand text-[10px] font-bold tabular-nums"
                  title={`Position ${job.position ?? '—'} in queue`}
                >
                  #{job.position ?? '—'}
                </span>
              )}
            </div>
          </div>

          {/* Message */}
          <p className="mt-1 text-xs leading-relaxed text-text-secondary break-words whitespace-pre-wrap">
            <TaggedContent
              content={job.user_message}
              maxChars={isLong && !isExpanded ? PREVIEW_BUDGET : undefined}
            />
          </p>
          {isLong && (
            <button
              type="button"
              onClick={onToggleExpand}
              className="mt-0.5 text-[10px] font-medium text-text-tertiary hover:text-text-brand-primary transition-colors"
            >
              {isExpanded ? 'Show less' : 'Show more'}
            </button>
          )}

          {/* Attachments */}
          {attachments.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {attachments.map((att: any, i: number) => (
                <AttachmentChip key={att.id ?? i} name={att.name ?? 'attachment'} fileType={att.fileType} />
              ))}
            </div>
          )}

          {/* Settings badges: interaction mode + model + any enabled agent settings */}
          {(job.interaction_mode || job.model_preference || activeSettings.length > 0) && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1">
              {job.interaction_mode && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-bg-brand-primary-alt text-text-brand-primary border border-border-brand capitalize">
                  {job.interaction_mode}
                </span>
              )}
              {job.model_preference && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-bg-secondary text-text-secondary border border-border-secondary capitalize">
                  <Icon name={IconName.Settings} size="xs" inheritColor className="text-text-tertiary" />
                  {job.model_preference}
                </span>
              )}
              {activeSettings.map(key => (
                <span
                  key={key}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-bg-success-primary text-text-success-primary border border-border-secondary"
                >
                  <Icon name={IconName.Check} size="xs" inheritColor />
                  {settingLabel(key)}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Actions — icon buttons, revealed on hover (always visible on touch) */}
        {canManage && (
          <div className="flex items-center gap-0.5 flex-shrink-0 -mr-1 opacity-60 group-hover:opacity-100 transition-opacity">
            {!isEditing && !isFailed && (
              <Button
                variant="ghost"
                size="very-small"
                label={<Icon name={IconName.Edit} size="xs" inheritColor />}
                onClick={onEdit}
                title="Edit this queued message"
                className="!p-1.5 !text-text-tertiary hover:!text-text-brand-primary"
              />
            )}
            <Button
              variant="ghost"
              size="very-small"
              label={<Icon name={IconName.Delete} size="xs" inheritColor />}
              onClick={onCancel}
              title={isFailed ? 'Dismiss failed task' : 'Remove from queue'}
              className="!p-1.5 !text-text-tertiary hover:!text-text-error-primary"
            />
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Renders the queued prompts as a panel attached to the top of the chat input.
 *
 * The panel header is a collapse toggle:
 * - Expanded (default): warning notices + scrollable job cards (max 280 px).
 * - Collapsed: only the one-line header is visible.
 */
export const QueuedMessageBanner: React.FC<QueuedMessageBannerProps> = ({
  jobs,
  onEdit,
  onCancel,
  onResumeAll,
  editingJobId,
  canEditProject = false,
  currentUserId,
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(true);

  if (jobs.length === 0) return null;

  const hasPaused = jobs.some(j => j.status === QueueStatus.PAUSED);
  // Queue is only ever paused because a preceding job failed — stops auto-advance instead.
  const isHalted = hasPaused;
  // Resume re-queues ALL paused jobs (project-wide), so it's an editor-only action.
  const canResume = hasPaused && !!onResumeAll && canEditProject;

  return (
    // Attached to the input: rounded top, flat bottom, sits flush on the input box.
    <div className="rounded-t-bg-v2-xl border border-b-0 border-border-secondary bg-bg-secondary container-shadow-md overflow-hidden">
      {/* ── Header — toggle on the left; Resume + count + chevron on the right.
            The Resume button is a sibling (not nested) so it stays clickable and
            visible even while the panel is collapsed. ─────────────────────────── */}
      <div className={`flex items-center gap-2 w-full px-3.5 py-2.5 ${isHalted ? 'bg-bg-error-primary' : ''}`}>
        <button
          type="button"
          onClick={() => setIsCollapsed(c => !c)}
          className="flex items-center gap-2 flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
          aria-expanded={!isCollapsed}
          aria-label={isCollapsed ? 'Expand queue' : 'Collapse queue'}
        >
          {isHalted ? (
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-bg-error-primary text-text-error-primary border border-border-error-subtle flex-shrink-0">
              <Icon name={IconName.Alert} size="xs" inheritColor />
            </span>
          ) : (
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-bg-brand-primary-alt text-text-brand-primary flex-shrink-0">
              {/* Inline stacked-layers glyph — no matching IconName */}
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                <path d="M8 1.5l6 3-6 3-6-3 6-3zM2 8l6 3 6-3M2 11.5l6 3 6-3"
                  stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          )}

          {isHalted ? (
            <span className="text-sm font-semibold text-text-error-primary truncate">
              Queue paused
            </span>
          ) : (
            <>
              <span className="text-sm font-semibold text-text-primary">
                {jobs.length} in queue
              </span>
              <span className="text-[11px] text-text-tertiary hidden sm:inline">
                · runs after the current task
              </span>
            </>
          )}
        </button>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Resume — the primary recovery action; always reachable from the header */}
          {canResume && (
            <Button
              variant="primary"
              size="very-small"
              label={
                <span className="inline-flex items-center gap-1">
                  <Icon name={IconName.Run} size="xs" inheritColor />
                  Resume
                </span>
              }
              onClick={onResumeAll}
              title="Re-queue the paused tasks and continue"
            />
          )}
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full tabular-nums bg-bg-tertiary text-text-tertiary">
            {jobs.length}
          </span>
          <button
            type="button"
            onClick={() => setIsCollapsed(c => !c)}
            className="flex items-center p-0.5 -mr-1 rounded hover:bg-bg-secondary-hover transition-colors"
            aria-label={isCollapsed ? 'Expand queue' : 'Collapse queue'}
          >
            <Icon
              name={IconName.ChevronDown}
              size="xs"
              inheritColor
              className={`text-text-tertiary flex-shrink-0 transition-transform duration-200 ${
                isCollapsed ? '-rotate-90' : 'rotate-0'
              }`}
            />
          </button>
        </div>
      </div>

      {/* ── Body (hidden when collapsed) ──────────────────────────────────── */}
      {!isCollapsed && (
        <div className="px-2.5 pb-2.5 flex flex-col gap-2">
          {/* Notices */}
          {/* Single consolidated notice. On a failure the rest auto-pause, so we
              explain both in one line; Resume lives in the header above. */}
          {isHalted && (
            <div className="text-[11px] text-text-error-primary bg-bg-error-primary border border-border-error-subtle px-3 py-2 rounded-lg flex items-start gap-2">
              <Icon name={IconName.Alert} size="xs" inheritColor className="mt-0.5 flex-shrink-0" />
              <span>
                <span className="font-medium">A preceding task failed.</span>{' '}
                Resume to continue the remaining tasks.
              </span>
            </div>
          )}

          {/* Job cards — scrollable, capped at ≈3 cards */}
          <div className="flex flex-col gap-2 max-h-[280px] overflow-y-auto pr-0.5 modern-scrollbar-spaced">
            {jobs.map(job => (
              <QueueCard
                key={job.job_id}
                job={job}
                isEditing={job.job_id === editingJobId}
                isFailed={job.status === QueueStatus.FAILED}
                isExpanded={expandedId === job.job_id}
                onToggleExpand={() => setExpandedId(expandedId === job.job_id ? null : job.job_id)}
                onEdit={() => onEdit(job.job_id)}
                onCancel={() => onCancel(job.job_id)}
                // Manageable if the viewer can edit the project, or owns this card.
                canManage={canEditProject || (!!currentUserId && job.user_info?.id === currentUserId)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default QueuedMessageBanner;
